import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@repo/ui/components/ui/sidebar";
import { Link } from "@tanstack/react-router";
import { Zap } from "lucide-react";
import { createContext, type ReactNode, use } from "react";

/** A patient link surfaced in a quick action. */
export interface ActivePatientLink {
  patientId: string;
  label: string;
}

/**
 * Shared state for the Quick Actions compound component. `collapsed` is true
 * only on the desktop icon-rail — actions read it to switch between an inline
 * dropdown (expanded) and a nested submenu (collapsed).
 */
const QuickActionsContext = createContext<{ collapsed: boolean } | null>(null);

function useQuickActions() {
  const ctx = use(QuickActionsContext);
  if (!ctx) throw new Error("Quick action components must be rendered within <NavQuickActions>");
  return ctx;
}

/**
 * "Quick Actions" sidebar group — a consolidated view of things that are live
 * for the current user. Actions compose in as children so new ones can be added
 * without touching this container.
 *
 * Expanded: each action is its own row. Collapsed to the icon rail: the whole
 * group folds into one icon whose dropdown lists the actions as submenus.
 */
export function NavQuickActions({ children }: { children: ReactNode }) {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;

  return (
    <QuickActionsContext value={{ collapsed }}>
      <SidebarGroup>
        {!collapsed && <SidebarGroupLabel>Quick Actions</SidebarGroupLabel>}
        <SidebarGroupContent className="px-1.5 md:px-0 group-data-[collapsible=icon]:px-px transition-[padding] duration-200 ease-linear">
          <SidebarMenu>
            {collapsed ? (
              <SidebarMenuItem>
                <DropdownMenu>
                  <SidebarMenuButton
                    render={<DropdownMenuTrigger />}
                    tooltip={{ children: "Quick Actions", hidden: false }}
                    className="px-2.5 md:px-2"
                  >
                    <Zap />
                    <span>Quick Actions</span>
                  </SidebarMenuButton>
                  <DropdownMenuContent className="min-w-56 rounded-lg" side="right" align="start" sideOffset={4}>
                    {children}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            ) : (
              children
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </QuickActionsContext>
  );
}

function QuickActionCount({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return <span className={`text-xs tabular-nums text-muted-foreground ${className ?? ""}`}>{count}</span>;
}

/**
 * A single quick action. Its items (list, empty state) are composed as
 * `children`; the trigger shape adapts to the sidebar's collapsed state.
 */
function QuickAction({ label, count, children }: { label: string; count?: number; children: ReactNode }) {
  const { collapsed } = useQuickActions();
  const { isMobile } = useSidebar();

  if (collapsed) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <span>{label}</span>
          {count !== undefined && <QuickActionCount count={count} className="ml-auto" />}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="min-w-56 rounded-lg">{children}</DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <SidebarMenuButton
          render={<DropdownMenuTrigger />}
          tooltip={{ children: label, hidden: false }}
          className="px-2.5 md:px-2"
        >
          <span>{label}</span>
          {count !== undefined && <QuickActionCount count={count} className="ml-auto" />}
        </SidebarMenuButton>
        <DropdownMenuContent
          className="min-w-56 rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align="start"
          sideOffset={4}
        >
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * Active Patients quick action — patients assigned to the current user that are
 * not discharged. Opens a menu of links to each patient.
 */
export function ActivePatientsAction({ patients }: { patients: ActivePatientLink[] }) {
  const items =
    patients.length === 0 ? (
      <DropdownMenuItem disabled>No active patients</DropdownMenuItem>
    ) : (
      patients.map((patient) => (
        <DropdownMenuItem
          key={patient.patientId}
          render={<Link to="/patients/$patientId" params={{ patientId: patient.patientId }} />}
        >
          <span className="truncate">{patient.label}</span>
        </DropdownMenuItem>
      ))
    );

  return (
    <QuickAction label="Active Patients" count={patients.length}>
      {items}
    </QuickAction>
  );
}
